import { inject, Injectable } from '@angular/core';
import { catchError, from, map, of, switchMap, throwError } from 'rxjs';
import { COMMO_SHARED_SUPABASE_CLIENT } from '../commo-shared-data-access.config';
import {
  COMMUNITY_ICON_MAX_BYTES,
  COMMUNITY_ICONS_BUCKET,
  communityIconStoragePath,
} from '../constants/community-icons';

@Injectable({ providedIn: 'root' })
export class CommunityIconsService {
  private readonly supabase = inject(COMMO_SHARED_SUPABASE_CLIENT);

  /**
   * DB の path / updated_at を先に更新してから JPEG を upsert する。
   * Storage 失敗時:
   * - 初回（hadExisting=false）→ DB をクリア（path だけのゴーストを残さない）
   * - 差し替え（hadExisting=true）→ DB はそのまま（旧ファイルは upsert 未成功のため残る）
   * UI は両方成功後にだけ再取得する前提。
   */
  uploadIcon(communityId: string, blob: Blob, hadExisting = false) {
    if (blob.size > COMMUNITY_ICON_MAX_BYTES) {
      return throwError(() => new Error('Icon file is too large.'));
    }
    const path = communityIconStoragePath(communityId);
    return from(
      this.supabase.rpc('set_community_icon', {
        p_community_id: communityId,
        p_clear: false,
      }),
    ).pipe(
      switchMap(({ error: rpcError }) => {
        if (rpcError) {
          return throwError(() => new Error(rpcError.message));
        }
        return from(
          this.supabase.storage
            .from(COMMUNITY_ICONS_BUCKET)
            .upload(path, blob, {
              upsert: true,
              contentType: 'image/jpeg',
              cacheControl: '3600',
            }),
        ).pipe(
          switchMap(({ error: uploadError }) => {
            if (!uploadError) return of(path);
            const fail = throwError(() => new Error(uploadError.message));
            if (hadExisting) return fail;
            // クリア失敗でも Storage のエラーを優先して返す
            return this.clearIconMeta(communityId).pipe(
              catchError(() => of(undefined)),
              switchMap(() => fail),
            );
          }),
        );
      }),
    );
  }

  /**
   * Storage を先に削除してから DB をクリアする。
   * 削除失敗時は DB を触らない（公開オブジェクトが残ったまま null にしない）。
   * RPC 失敗時は一度だけリトライ（実体は既に無いため DB を合わせる）。
   */
  clearIcon(communityId: string) {
    const path = communityIconStoragePath(communityId);
    return from(
      this.supabase.storage.from(COMMUNITY_ICONS_BUCKET).remove([path]),
    ).pipe(
      switchMap(({ error: removeError }) => {
        if (removeError) {
          return throwError(() => new Error(removeError.message));
        }
        return this.clearIconMeta(communityId);
      }),
    );
  }

  private clearIconMeta(communityId: string) {
    const call = () =>
      from(
        this.supabase.rpc('set_community_icon', {
          p_community_id: communityId,
          p_clear: true,
        }),
      );

    return call().pipe(
      switchMap(({ error }) => {
        if (!error) return of(undefined);
        return call().pipe(
          map(({ error: retryError }) => {
            if (retryError) throw new Error(retryError.message);
            return undefined;
          }),
        );
      }),
    );
  }
}
